import { useEffect, useMemo, useState } from 'react';
import {
  REPORT_KINDS,
  reportTable,
  sortReportRows,
  tableToCsv,
  type ReportKind,
  type ReportRowsByKind,
} from '@shared/domain/reports.ts';
import { ROUNDING_OPTIONS, type Rounding } from '@shared/domain/rounding.ts';
import { api } from '../lib/api.ts';
import { friendlyMessage } from '../lib/errors.ts';
import { currentWeekRange } from '../lib/format.ts';
import { store, useApp } from '../state/store.ts';

const NUMERIC = new Set([
  'total_seconds',
  'horas_estimadas',
  'horas_reales',
  'diferencia',
  'minutes',
]);

function normalize(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((r) =>
    Object.fromEntries(
      Object.entries(r).map(([k, v]) => [k, NUMERIC.has(k) && v !== null ? Number(v) : v]),
    ),
  );
}

export function ReportsPage() {
  const { profile, online } = useApp();
  const [kind, setKind] = useState<ReportKind>('persona_semana');
  const [range, setRange] = useState(currentWeekRange);
  const [rounding, setRounding] = useState<Rounding>(profile?.redondeo ?? 'ninguno');
  const key = `${kind}|${range.from}|${range.to}|${rounding}`;
  const [result, setResult] = useState<{
    key: string;
    rows: Record<string, unknown>[];
    error: string | null;
  } | null>(null);

  useEffect(() => {
    if (!online || range.from > range.to) return;
    let cancelled = false;
    api
      .report(kind, range.from, range.to, rounding)
      .then((data) => {
        if (!cancelled) setResult({ key, rows: normalize(data), error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled) setResult({ key, rows: [], error: friendlyMessage(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [key, kind, range.from, range.to, rounding, online]);

  const current = result?.key === key ? result : null;
  const rows = useMemo(() => current?.rows ?? [], [current]);
  const state = {
    loading: online && range.from <= range.to && !current,
    error: current?.error ?? null,
  };

  const table = useMemo(
    () =>
      reportTable(kind, sortReportRows(kind, rows as unknown as ReportRowsByKind[typeof kind][])),
    [kind, rows],
  );

  function downloadCsv() {
    const blob = new Blob([tableToCsv(table)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tablero-${kind}-${range.from}-${range.to}.csv`;
    document.body.append(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="reports">
      <h1>Reportes</h1>
      <div className="tabs" role="tablist" aria-label="Tipo de reporte">
        {REPORT_KINDS.map((k) => (
          <button
            key={k.kind}
            type="button"
            role="tab"
            aria-selected={kind === k.kind}
            onClick={() => setKind(k.kind)}
          >
            {k.label}
          </button>
        ))}
      </div>
      <div className="grid-3 report-filters">
        <label>
          Desde
          <input
            type="date"
            value={range.from}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
          />
        </label>
        <label>
          Hasta
          <input
            type="date"
            value={range.to}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
          />
        </label>
        <label>
          Redondeo
          <select
            value={rounding}
            onChange={(e) => {
              const next = e.target.value as Rounding;
              setRounding(next);
              void store.setRedondeo(next);
            }}
          >
            {ROUNDING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted small">
        Zona horaria America/Santiago; semanas de lunes a domingo. Cada registro cuenta en el día en
        que empezó.
      </p>
      {!online && <p className="banner banner-offline">Los reportes necesitan conexión.</p>}
      {range.from > range.to && (
        <p className="form-error">La fecha "Desde" debe ser anterior a "Hasta".</p>
      )}
      {state.error && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      <div className="table-wrap">
        <table className="report-table" aria-busy={state.loading}>
          <thead>
            <tr>
              {table.headers.map((h) => (
                <th key={h} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.length === 0 && (
              <tr>
                <td colSpan={table.headers.length} className="muted">
                  {state.loading ? 'Cargando…' : 'Sin registros en el rango.'}
                </td>
              </tr>
            )}
            {table.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              {table.footer.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <button type="button" className="button" onClick={downloadCsv} disabled={state.loading}>
        Descargar CSV
      </button>
    </div>
  );
}
